import { useNavigate } from 'react-router-dom';
import { FiX, FiFolder, FiCheckSquare, FiUsers } from 'react-icons/fi';
import { getWorkItemPath } from '@/utils/workItemPath';
import { WorkItemType } from '@/types';

interface SearchResult {
  id: string;
  type: 'task' | 'project' | 'user';
  title: string;
  subtitle?: string;
  url: string;
  sequenceNumber?: number;
}

interface SearchResultsProps {
  query: string;
  results: {
    tasks: any[];
    projects: any[];
    users: any[];
  };
  isLoading: boolean;
  onClose: () => void;
  onNavigate: (url: string) => void;
}

export default function SearchResults({ query, results, isLoading, onClose, onNavigate }: SearchResultsProps) {
  const navigate = useNavigate();

  const allResults: SearchResult[] = [
    ...results.tasks.map((task: any) => ({
      id: task.id,
      type: 'task' as const,
      title: task.title,
      subtitle: task.project?.name || 'No project',
      url: getWorkItemPath({
        id: task.id,
        type: (task.type as WorkItemType) || WorkItemType.TASK,
        sequenceNumber: task.sequenceNumber,
      }),
      sequenceNumber: task.sequenceNumber,
    })),
    ...results.projects.map((project: any) => ({
      id: project.id,
      type: 'project' as const,
      title: project.name,
      subtitle: project.description || 'Project',
      url: `/board/${project.id}`,
    })),
    ...results.users.map((user: any) => ({
      id: user.id,
      type: 'user' as const,
      title: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      subtitle: user.email,
      url: `/members`,
    })),
  ];

  const handleResultClick = (result: SearchResult) => {
    onNavigate(result.url);
    navigate(result.url);
    onClose();
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'task':
        return <FiCheckSquare className="w-4 h-4" />;
      case 'project':
        return <FiFolder className="w-4 h-4" />;
      case 'user':
        return <FiUsers className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getIconBgColor = (type: string) => {
    switch (type) {
      case 'task':
        return 'bg-blue-100 text-blue-600';
      case 'project':
        return 'bg-green-100 text-green-600';
      case 'user':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (!query.trim()) {
    return null;
  }

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-[500px] overflow-hidden z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            Search results for "{query}"
          </span>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          title="Close search"
        >
          <FiX className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Results */}
      <div className="overflow-y-auto max-h-[440px]">
        {allResults.length === 0 && !isLoading && (
          <div className="px-4 py-8 text-center text-gray-500">
            <p className="text-sm">No results found</p>
            <p className="text-xs mt-1">Try a different search term</p>
          </div>
        )}

        {/* Group by type */}
        {results.projects.length > 0 && (
          <div className="border-b border-gray-100">
            <div className="px-4 py-2 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Projects ({results.projects.length})
              </p>
            </div>
            {results.projects.map((project: any) => (
              <button
                key={`project-${project.id}`}
                onClick={() => handleResultClick({
                  id: project.id,
                  type: 'project',
                  title: project.name,
                  subtitle: project.description,
                  url: `/board/${project.id}`,
                })}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className={`p-2 rounded-lg ${getIconBgColor('project')}`}>
                  {getIcon('project')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
                  {project.description && (
                    <p className="text-xs text-gray-500 truncate">{project.description}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {results.tasks.length > 0 && (
          <div className="border-b border-gray-100">
            <div className="px-4 py-2 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Tasks ({results.tasks.length})
              </p>
            </div>
            {results.tasks.map((task: any) => (
              <button
                key={`task-${task.id}`}
                onClick={() => handleResultClick({
                  id: task.id,
                  type: 'task',
                  title: task.title,
                  subtitle: task.project?.name || 'No project',
                  url: getWorkItemPath({
                    id: task.id,
                    type: (task.type as WorkItemType) || WorkItemType.TASK,
                    sequenceNumber: task.sequenceNumber,
                  }),
                })}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className={`p-2 rounded-lg ${getIconBgColor('task')}`}>
                  {getIcon('task')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                    {task.sequenceNumber != null && (
                      <span className="shrink-0 text-xs text-gray-400 font-mono">#{task.sequenceNumber}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{task.project?.name || 'No project'}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {results.users.length > 0 && (
          <div>
            <div className="px-4 py-2 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                People ({results.users.length})
              </p>
            </div>
            {results.users.map((user: any) => (
              <button
                key={`user-${user.id}`}
                onClick={() => handleResultClick({
                  id: user.id,
                  type: 'user',
                  title: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                  subtitle: user.email,
                  url: `/members`,
                })}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className={`p-2 rounded-lg ${getIconBgColor('user')}`}>
                  {getIcon('user')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500">
          Press <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">ESC</kbd> to close
        </p>
      </div>
    </div>
  );
}
